/**
 * @meowntain/web entry point — Milestone 2 Arcade greybox.
 */

import { bootGame } from './game.js';
import './style.css';

const app = document.getElementById('app');
if (!app) throw new Error('#app element missing');

app.innerHTML = `
  <div class="game-shell">
    <div id="canvas-host" class="canvas-host"></div>
    <div class="hud" aria-live="polite">
      <div class="hud-row">
        <span class="hud-label">cats</span>
        <span class="hud-value" id="hud-count">0</span>
      </div>
      <div class="hud-row">
        <span class="hud-label">height</span>
        <span class="hud-value" id="hud-height">0</span>
      </div>
      <div class="hud-row">
        <span class="hud-label">score</span>
        <span class="hud-value" id="hud-score">0</span>
      </div>
      <div class="hud-row">
        <span class="hud-label">treats</span>
        <span class="hud-value" id="hud-treats">8</span>
      </div>
      <div class="hud-row">
        <span class="hud-label">nip</span>
        <span class="hud-value" id="hud-catnip">3</span>
      </div>
    </div>
    <div class="meters" aria-hidden="true">
      <div class="meter-block">
        <span class="meter-label">sleeper</span>
        <div class="meter" id="hud-person-meter"><div class="meter-fill meter-fill--person"></div></div>
      </div>
      <div class="meter-block">
        <span class="meter-label">cats</span>
        <div class="meter" id="hud-cat-meter"><div class="meter-fill meter-fill--cat"></div></div>
      </div>
    </div>
    <div class="pantry" role="group" aria-label="Pantry">
      <button type="button" class="pantry-btn" id="hud-treat-btn">Treat</button>
      <button type="button" class="pantry-btn pantry-btn--catnip" id="hud-catnip-btn">Catnip</button>
      <button type="button" class="pantry-btn pantry-btn--hide-toy" id="hud-hide-toy-btn" hidden>Hide toy</button>
    </div>
    <div class="wake-overlay" id="wake-overlay" hidden>
      <p class="wake-message" id="wake-message"></p>
    </div>
    <p class="hint" id="hud-hint">tap above the bed to drop a cat</p>
  </div>
`;

const canvasHost = document.getElementById('canvas-host');
const hudCount = document.getElementById('hud-count');
const hudHeight = document.getElementById('hud-height');
const hudScore = document.getElementById('hud-score');
const hudTreats = document.getElementById('hud-treats');
const hudCatnip = document.getElementById('hud-catnip');
const hudPersonMeter = document.getElementById('hud-person-meter');
const hudCatMeter = document.getElementById('hud-cat-meter');
const hudTreatBtn = document.getElementById('hud-treat-btn');
const hudCatnipBtn = document.getElementById('hud-catnip-btn');
const hudHideToyBtn = document.getElementById('hud-hide-toy-btn');
const wakeOverlay = document.getElementById('wake-overlay');
const wakeMessage = document.getElementById('wake-message');
const hudHint = document.getElementById('hud-hint');

if (
  !canvasHost ||
  !hudCount ||
  !hudHeight ||
  !hudScore ||
  !hudTreats ||
  !hudCatnip ||
  !hudPersonMeter ||
  !hudCatMeter ||
  !hudTreatBtn ||
  !hudCatnipBtn ||
  !hudHideToyBtn ||
  !wakeOverlay ||
  !wakeMessage ||
  !hudHint
) {
  throw new Error('HUD elements missing');
}

function showBootError(err: unknown): void {
  const host = canvasHost as HTMLElement;
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  const stack = err instanceof Error && err.stack ? err.stack : '';
  host.innerHTML = `
    <div class="boot-error" role="alert">
      <h2>Something broke during boot.</h2>
      <p class="boot-error-msg">${escapeHtml(msg)}</p>
      ${stack ? `<details><summary>stack</summary><pre>${escapeHtml(stack)}</pre></details>` : ''}
      <p class="boot-error-help">Open the browser console for full details.</p>
    </div>
  `;
  if (hudHint) hudHint.textContent = '';
  console.error('[meowntain] boot failed:', err);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

window.addEventListener('error', (ev) => showBootError(ev.error ?? ev.message));
window.addEventListener('unhandledrejection', (ev) => showBootError(ev.reason));

try {
  const game = await bootGame(canvasHost, {
    count: hudCount,
    height: hudHeight,
    score: hudScore,
    treats: hudTreats,
    catnip: hudCatnip,
    personMeter: hudPersonMeter,
    catMeter: hudCatMeter,
    treatButton: hudTreatBtn as HTMLButtonElement,
    catnipButton: hudCatnipBtn as HTMLButtonElement,
    hideToyButton: hudHideToyBtn as HTMLButtonElement,
    wakeOverlay: wakeOverlay as HTMLElement,
    wakeMessage: wakeMessage as HTMLElement,
    hint: hudHint,
  });
  await game.start();

  if (import.meta.hot) {
    import.meta.hot.dispose(() => game.destroy());
  }
} catch (err) {
  showBootError(err);
}
